// Pure deadline/countdown math for the submission flow.
//
// All decisions run on server time: every deadline-bearing response carries
// serverTimeUtc, we capture (serverTime - clientNow) once as an offset, and
// later renders apply that offset — so a wrong device clock can neither hide
// the expired gate nor trigger it. Display rules (docs/13, Phase 0):
//   >= 48h  "Closes in N days" (ceil)
//   24-48h  "Closes tomorrow"
//   1-24h   "Closes in N hours" (ceil)
//   < 1h    "Closes in N minutes" (ceil)
//   <= 0    "Expired" (inclusive at the boundary — never "-1 days")
//
// "Expired", deliberately, not "Closed": those are two different states and the
// difference is visible to the visitor. A breakdown whose `status` is "Closed"
// is over — casting shut it. A breakdown still marked "Open" whose closing date
// has passed has expired on the clock, and only the clock. Labelling the second
// one "Closed" claims something about the posting that the posting does not
// say. Pages that know the status say "Closed" from the status itself.

import i18next from "i18next";

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export interface IDeadlineInfo {
  isClosed: boolean;
  remainingMs: number;
  label: string;
}

// Offset between the server clock and this device, captured at fetch time.
export const getServerOffsetMs = (
  serverTimeUtc: string,
  clientNowMs: number = Date.now()
): number => {
  const serverMs = Date.parse(serverTimeUtc);
  return isNaN(serverMs) ? 0 : serverMs - clientNowMs;
};

export const getDeadlineInfo = (
  closingDateUtc: string,
  serverOffsetMs: number = 0,
  clientNowMs: number = Date.now()
): IDeadlineInfo => {
  const closingMs = Date.parse(closingDateUtc);
  const nowMs = clientNowMs + serverOffsetMs;
  const remainingMs = isNaN(closingMs) ? 0 : closingMs - nowMs;

  if (remainingMs <= 0) {
    return { isClosed: true, remainingMs: 0, label: i18next.t("Expired") };
  }
  if (remainingMs < HOUR_MS) {
    const minutes = Math.ceil(remainingMs / MINUTE_MS);
    return {
      isClosed: false,
      remainingMs,
      label:
        minutes === 1
          ? i18next.t("Closes in 1 minute")
          : i18next.t("Closes in {{count}} minutes", { count: minutes }),
    };
  }
  if (remainingMs < DAY_MS) {
    const hours = Math.ceil(remainingMs / HOUR_MS);
    return {
      isClosed: false,
      remainingMs,
      label:
        hours === 1
          ? i18next.t("Closes in 1 hour")
          : i18next.t("Closes in {{count}} hours", { count: hours }),
    };
  }
  if (remainingMs < 2 * DAY_MS) {
    return { isClosed: false, remainingMs, label: i18next.t("Closes tomorrow") };
  }
  const days = Math.ceil(remainingMs / DAY_MS);
  return {
    isClosed: false,
    remainingMs,
    label: i18next.t("Closes in {{count}} days", { count: days }),
  };
};
